import * as React from 'react';

import type { SeniorityLevelDto, TjmEntryTargetDto } from '@/api/catalog-client';
import { useCurrentCatalogReferential, useTjmEntryTarget } from '@/api/use-catalog';
import { formatCatalogDate } from '@/lib/catalog-labels';
import {
  buildFamilyTree,
  correlatedProfiles,
  filterProfiles,
  indexByCode,
  type CatalogReferentialSearch,
} from '@/routes/supplier/catalog/catalog-referential-model';
import { CatalogFilters } from '@/routes/supplier/catalog/catalog-filters';
import { FamilyNav } from '@/routes/supplier/catalog/family-nav';
import { ProfileDetailPanel } from '@/routes/supplier/catalog/profile-detail-panel';
import { ProfileList } from '@/routes/supplier/catalog/profile-list';

// =============================================================================
// catalog-referential-screen.tsx — Uc-CAT-PROFILS : consultation du référentiel des profils (DBU §3.4).
// =============================================================================
// DBU §3.4 l.2658 : « référentiel COMPLET des profils JeKo et de leurs niveaux d'expertise »,
// consultation seule. Écran FOURNISSEUR (monde supplier), 3 ZONES (l.2686/2698) :
//   • menu latéral GAUCHE — navigation par familles / sous-familles (FamilyNav) ;
//   • barre HAUTE — recherche + filtres combinables + réinitialiser (CatalogFilters) ;
//   • zone CENTRALE — liste des profils (ProfileList) ; fiche détaillée en panneau latéral DROIT
//     (ProfileDetailPanel) avec chips de profils corrélés cliquables.
//
// LECTURE SEULE TOUJOURS (DBU §3.4 l.2730/2736/2738) : aucune édition / création / archivage, aucun
// export, aucun CTA de saisie. L'AUTORITÉ (PUBLISHED-only, RBAC supplier) est l'API.
//
// L'ÉTAT DE VUE (famille / sousFamille / niveau / q / profil) est porté par l'URL (search params
// TanStack ; RULE_REF_006 : jamais localStorage) : le composant est CONTRÔLÉ (props `search` +
// `onChange`), l'adaptateur routeur branche l'URL. Le filtrage/tri est délégué à des sélecteurs PURS
// (catalog-referential-model) → rendu déterministe et fluide.
//
// WCAG 2.1 AA : titres hiérarchisés, états pending role="status" / erreur role="alert" / vide
// role="status", nav + filtres + badges accessibles (portés par leurs sous-composants).

export interface CatalogReferentialScreenProps {
  /** État de vue courant (issu de l'URL). */
  readonly search: CatalogReferentialSearch;
  /** Remplace l'état de vue (l'adaptateur routeur le sérialise dans l'URL). */
  readonly onChange: (next: CatalogReferentialSearch) => void;
  /**
   * R15-CMP-03 — deep-link vers la cellule de saisie du profil : `(target, profileCode)` du CTA « Saisir mon
   * TJM ». Fourni par l'adaptateur routeur (navigation cross-route vers `/saisie/$submissionId?profil=…`).
   * Optionnel : hors routeur (UT contrôlés), le CTA reste rendu mais inerte.
   */
  readonly onSaisirTjm?: (target: TjmEntryTargetDto, profileCode: string) => void;
}

export function CatalogReferentialScreen({
  search,
  onChange,
  onSaisirTjm,
}: CatalogReferentialScreenProps): React.JSX.Element {
  const query = useCurrentCatalogReferential();

  // --- Handlers dérivés (chaque changement remplace l'état de vue complet → URL) -------------
  // `patch` fusionne puis COMPACTE (drop des clés `undefined`) : sous `exactOptionalPropertyTypes`,
  // une clé absente ≠ une clé à `undefined`. L'URL reste propre (aucun `?famille=undefined`).
  const patch = React.useCallback(
    (
      changes: Partial<{
        famille: string | undefined;
        sousFamille: string | undefined;
        niveau: SeniorityLevelDto | undefined;
        q: string | undefined;
        profil: string | undefined;
      }>,
    ): void => {
      const merged = { ...search, ...changes };
      const next: {
        famille?: string;
        sousFamille?: string;
        niveau?: SeniorityLevelDto;
        q?: string;
        profil?: string;
      } = {};
      if (merged.famille !== undefined) next.famille = merged.famille;
      if (merged.sousFamille !== undefined) next.sousFamille = merged.sousFamille;
      if (merged.niveau !== undefined) next.niveau = merged.niveau;
      if (merged.q !== undefined && merged.q.length > 0) next.q = merged.q;
      if (merged.profil !== undefined) next.profil = merged.profil;
      onChange(next);
    },
    [onChange, search],
  );

  const onSelectFamille = React.useCallback(
    (famille: string | undefined): void => {
      // Changer de famille invalide la sous-famille précédente (hors périmètre de la nouvelle).
      patch({ famille, sousFamille: undefined });
    },
    [patch],
  );
  const onSelectSousFamille = React.useCallback(
    (sousFamille: string | undefined): void => {
      patch({ sousFamille });
    },
    [patch],
  );
  const onSelectNiveau = React.useCallback(
    (niveau: SeniorityLevelDto | undefined): void => {
      patch({ niveau });
    },
    [patch],
  );
  const onSearch = React.useCallback(
    (q: string): void => {
      patch({ q });
    },
    [patch],
  );
  const onReset = React.useCallback((): void => {
    // Réinitialise TOUS les filtres ; préserve la fiche ouverte (le panneau `profil` est indépendant).
    onChange(search.profil === undefined ? {} : { profil: search.profil });
  }, [onChange, search]);
  const onSelectProfile = React.useCallback(
    (profileCode: string): void => {
      patch({ profil: profileCode });
    },
    [patch],
  );
  const onCloseProfile = React.useCallback((): void => {
    patch({ profil: undefined });
  }, [patch]);

  // --- Dérivations pures (mémoïsées : rendu déterministe et fluide) --------------------------
  const referential = query.data;
  const profiles = React.useMemo(
    () => referential?.profiles ?? [],
    [referential],
  );
  const correlations = React.useMemo(
    () => referential?.correlations ?? [],
    [referential],
  );

  const familles = React.useMemo(() => buildFamilyTree(profiles), [profiles]);
  const byCode = React.useMemo(() => indexByCode(profiles), [profiles]);
  const filtered = React.useMemo(
    () => filterProfiles(profiles, search),
    [profiles, search],
  );
  const selectedProfile =
    search.profil === undefined ? null : (byCode.get(search.profil) ?? null);
  const correlated = React.useMemo(
    () =>
      selectedProfile === null
        ? []
        : correlatedProfiles(selectedProfile.profileCode, correlations, byCode),
    [selectedProfile, correlations, byCode],
  );

  // R15-CMP-03 / F0-CONTRACT-4 — cible de saisie TJM du profil ouvert (fournisseur en session). Query
  // DÉSACTIVÉE tant qu'aucune fiche n'est ouverte (`profileCode` undefined). `null` ⇒ pas de CTA. Le clic
  // deep-linke vers la cellule de saisie via l'adaptateur routeur (`onSaisirTjm`).
  const tjmEntryQuery = useTjmEntryTarget(selectedProfile?.profileCode);
  const tjmEntryTarget = tjmEntryQuery.data ?? null;
  const handleSaisirTjm = React.useCallback((): void => {
    if (selectedProfile !== null && tjmEntryTarget !== null) {
      onSaisirTjm?.(tjmEntryTarget, selectedProfile.profileCode);
    }
  }, [onSaisirTjm, selectedProfile, tjmEntryTarget]);

  const hasActiveFilters =
    search.famille !== undefined ||
    search.sousFamille !== undefined ||
    search.niveau !== undefined ||
    (search.q !== undefined && search.q.length > 0);

  return (
    <section className="flex flex-col gap-6" aria-labelledby="catalog-referential-title">
      <header className="flex flex-col gap-1">
        <h1
          id="catalog-referential-title"
          className="text-2xl font-semibold tracking-tight text-foreground"
        >
          Catalogue des profils
        </h1>
        <p className="text-sm text-muted-foreground">
          Référentiel complet des profils JeKo et de leurs niveaux d'expertise. Consultation seule.
        </p>
        {query.isSuccess && referential?.version !== null && referential !== undefined ? (
          <p className="text-sm text-muted-foreground">
            Version {referential.version.versionNumber}
            {referential.version.publishedAt !== null
              ? ` · publiée le ${formatCatalogDate(referential.version.publishedAt)}`
              : ''}
          </p>
        ) : null}
      </header>

      {query.isPending ? (
        <p role="status" className="text-sm text-muted-foreground">
          Chargement du référentiel des profils…
        </p>
      ) : null}

      {query.isError ? (
        <p
          role="alert"
          className="rounded-md border border-destructive/50 bg-destructive/10 px-4 py-3 text-sm text-foreground"
        >
          Impossible de charger le référentiel des profils : {query.error.message}
        </p>
      ) : null}

      {query.isSuccess && referential?.version === null ? (
        <p
          role="status"
          className="rounded-md border border-dashed border-border px-4 py-8 text-center text-sm text-muted-foreground"
        >
          Aucune version publiée. Le référentiel des profils sera consultable ici dès la publication
          d'une version du catalogue.
        </p>
      ) : null}

      {query.isSuccess && referential?.version !== null && referential !== undefined ? (
        <div className="grid gap-6 lg:grid-cols-[16rem_1fr]">
          {/* <div> (pas <aside>) : le <nav> interne porte déjà le landmark ; un aside imbriqué
              dans la region <section> violerait landmark-complementary-is-top-level (WCAG). */}
          <div className="lg:sticky lg:top-6 lg:self-start">
            <FamilyNav
              familles={familles}
              selectedFamille={search.famille}
              selectedSousFamille={search.sousFamille}
              onSelectFamille={onSelectFamille}
              onSelectSousFamille={onSelectSousFamille}
            />
          </div>

          <div className="flex flex-col gap-6">
            <CatalogFilters
              familles={familles}
              search={search.q}
              famille={search.famille}
              sousFamille={search.sousFamille}
              niveau={search.niveau}
              onSearch={onSearch}
              onSelectFamille={onSelectFamille}
              onSelectSousFamille={onSelectSousFamille}
              onSelectNiveau={onSelectNiveau}
              onReset={onReset}
              hasActiveFilters={hasActiveFilters}
            />
            <ProfileList
              profiles={filtered}
              selectedProfileCode={search.profil}
              onSelectProfile={onSelectProfile}
            />
          </div>
        </div>
      ) : null}

      <ProfileDetailPanel
        profile={selectedProfile}
        correlated={correlated}
        onSelectCorrelated={onSelectProfile}
        onClose={onCloseProfile}
        tjmEntryTarget={tjmEntryTarget}
        onSaisirTjm={handleSaisirTjm}
      />
    </section>
  );
}
